// React + vendor imports
import { FunctionComponent } from "react";
import { Button, Modal } from "react-bootstrap";

// App interfaces
import { I_IncidentAction, I_SafetyEventWithApprovalHistory } from "../../interfaces";

// App services and utilities
import VIS from "../../VehicleIncidentsService";
import approvalService from "src/components/approvals/ApprovalService";
import vehicleIncidentsStatusAliases from "../../constants/VehicleIncidentsStatusAliases";

// Shared component
import FetchPortalAPIGeneratedPDF from "src/components/common/FetchPortalAPIGeneratedPDF/FetchPortalAPIGeneratedPDF";

// Styles
import "./VehicleIncidentsFetchPortalAPIGeneratedPDF.css";

// Props
interface Props {
  incidentAction: I_IncidentAction;
}

/**
 * VehicleIncidentsFetchPortalAPIGeneratedPDF
 * ------------------------------------------
 * Displays a modal containing a generated PDF preview
 * for a specific vehicle incident (Samsara).
 *
 * Responsibilities:
 * - Retrieves and prepares the incident data
 * - Applies alias mappings to approval history
 * - Displays modal with preview via <FetchPortalAPIGeneratedPDF>
 */
const VehicleIncidentsFetchPortalAPIGeneratedPDF: FunctionComponent<Props> = ({ incidentAction }) => {
  const { retrieveEventFromMap, setIncidentAction } = VIS;
  const { retrieveEmployeeFromMap, applyAliasesToHistory } = approvalService;

  // Get the incident data for the selected ID
  const incidentData = retrieveEventFromMap(incidentAction.id) as I_SafetyEventWithApprovalHistory;

  // Apply status aliases to approval history (if it exists)
  const aliasedIncidentData = incidentData
    ? {
      ...incidentData,
      ApprovalHistory: incidentData.ApprovalHistory
        ? applyAliasesToHistory(vehicleIncidentsStatusAliases, incidentData.ApprovalHistory)
        : [],
    }
    : null;

  // Get associated employee info for display
  const employee = retrieveEmployeeFromMap(incidentData?.EmployeeID ?? -1);

  return (
    <Modal
      id="vehicle-incidents-fetch-pdf-modal"
      show
      backdrop="static"
      keyboard
      centered
      size="xl"
      animation={false}
      className="VehicleIncidentsWorkflowModal"
    >
      <Modal.Header>
        <div className="VehicleIncidentsFetchPortalAPIGeneratedPDFHeader VehicleIncidentsWorkflowModalHeader">
          <div className="VehicleIncidentsFetchPortalAPIGeneratedPDFHeaderLeft">
            <p>
              Generated Vehicle Incident PDF for{" "}
              <span>{employee?.FirstName} {employee?.LastName}</span>
            </p>
          </div>
          <div className="VehicleIncidentsFetchPortalAPIGeneratedPDFHeaderRight">
            <span className="material-icons">picture_as_pdf</span>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className="VehicleIncidentsFetchPortalAPIGeneratedPDFModalBody">
          {aliasedIncidentData && (
            <FetchPortalAPIGeneratedPDF
              pdfData={{
                template: "samsaraIncident",
                data: aliasedIncidentData,
              }}
            />
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="VehicleIncidentsFetchPortalAPIGeneratedPDFModalFooter VehicleIncidentsGenericModalFooter">
          <div className="VehicleIncidentsFetchPortalAPIGeneratedPDFModalFooterLeft">

          </div>
          <div className="VehicleIncidentsFetchPortalAPIGeneratedPDFModalFooterRight">
            <Button
              variant="secondary"
              onClick={() => setIncidentAction({ id: -1, action: -1 })}
            >
              Close
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default VehicleIncidentsFetchPortalAPIGeneratedPDF;